import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, DropdownButton, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { OfferFilter } from './OfferFilter';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { OfferService } from '../OfferService';
import { formatDate, getStatusLabel, hasAccessRight } from '../../../utils/Functions';
import { ErrorToast } from '../../../utils/toasters';
import { Offer } from '../../../model/offer/Offer';
import DropdownItem from 'react-bootstrap/DropdownItem';
import { useNavigate } from 'react-router';
import { AddAdditionalItemModal } from './AddAdditionalModal';
import { toast } from 'react-toastify';
import { UPDATED_OFFER } from '../../../enum/Messages';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { OfferStatusEnum } from '../../../enum/OfferStatusEnum';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { PurchaseOrderModal } from '../../../shared/components/PurchaseOrderModal';
import { downloadFileRequest } from '../../../api/api.service';
import { OverviewModal } from '../../../shared/components/OverviewModal';
import { LIST_OFFER_URL, OFFER_LIST_DOWNLOAD_URL } from '../../../utils/Endpoints';

const OFFER_COLUMNS: ITableColumn[] = [
    { label: 'Date' },
    { label: 'Offer ID' },
    { label: 'Company' },
    { label: 'Influencer' },
    { label: 'Campaign' },
    { label: 'Status' },
    { label: 'Type' },
    { label: 'Price' },
    { label: 'Purchase Order' },
    { label: 'Agency?' },
    { label: 'Current?' },
    { label: 'Action' },
];

export function OfferList() {
    const navigate = useNavigate();
    const [dataLoading, setDataLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [overviewOfferId, setOverviewOfferId] = useState<number | null>(null);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 15,
        id: '',
        date: '',
        influencerId: '',
        campaignId: '',
        companyId: '',
        status: '',
        totalPages: 0,
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
    const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
    const [purchaseOrderOffer, setPurchaseOrderOffer] = useState<Offer | null>(null);

    useEffect(() => {
        fetchOffers(filters);
    }, [filters.campaignId, filters.influencerId, filters.companyId]);

    const fetchOffers = (params: typeof filters, pageOverride?: number) => {
        setDataLoading(true);
        const page = pageOverride ?? params.page;

        OfferService.getOffers({ ...params, page })
            .then(({ data, page: responsePage, count }) => {
                const totalPages = Math.ceil(count / params.perPage);

                if (totalPages > 0 && page > totalPages) {
                    fetchOffers({ ...params, page: 1 });
                    return;
                }

                setOffers(data);
                setFilters(() => ({
                    ...params,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(ErrorToast)
            .finally(() => setDataLoading(false));
    };

    const handleExport = () => {
        const params = Object.fromEntries(
            Object.entries(filters).filter(
                ([key, value]) =>
                    !['page', 'perPage', 'totalPages'].includes(key) && value !== '' && value !== undefined
            )
        );
        downloadFileRequest(OFFER_LIST_DOWNLOAD_URL, 'export-offers', null, params, setIsExporting);
    };

    const handlePageChange = (page: number) => {
        fetchOffers(filters, page);
    };

    const getOfferType = (isAddonsOnly?: boolean, isLongTerm?: boolean) =>
        isAddonsOnly ? 'Add-ons' : isLongTerm ? 'Spending' : 'Normal';

    const renderStatusIcon = (condition?: boolean) =>
        condition ? (
            <span className="word-break text-primary">
                <FaCheck />
            </span>
        ) : (
            <span className="word-break text-danger">
                <FaTimes />
            </span>
        );

    const renderWithTooltip = (text?: string) =>
        text ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="info-tooltip">{text}</Tooltip>}>
                <span className="ml-1">
                    <FiInfo size={14} className="cursor-pointer" />
                </span>
            </OverlayTrigger>
        ) : null;

    const handleAddAdditional = async (data: any) => {
        if (selectedOffer) {
            OfferService.addAdditionalItem(selectedOffer.id, { ...data })
                .then(() => {
                    toast(UPDATED_OFFER);
                })
                .catch(err => ErrorToast(err));
        }
    };

    const handleSavePurchaseOrder = (purchaseOrderInfluencer: string) => {
        if (!purchaseOrderOffer) return;

        OfferService.updatePurchaseOrder(purchaseOrderOffer.id, purchaseOrderInfluencer)
            .then(() => {
                toast(UPDATED_OFFER);
                fetchOffers(filters);
            })
            .catch(err => ErrorToast(err));
    };
    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') fetchOffers(filters);
                }}
            >
                <OfferFilter
                    handleState={fetchOffers}
                    setState={setFilters}
                    offersFilter={filters}
                    onExport={handleExport}
                    isExporting={isExporting}
                    isDisabled={dataLoading}
                />
            </Card>

            <OverviewModal
                show={overviewOfferId !== null}
                id={overviewOfferId}
                title="Offer"
                fetchOverview={OfferService.getOfferOverview}
                storedPdfUrl={pdfId => `${LIST_OFFER_URL}/pdf-file/${pdfId}`}
                generatedPdfUrl={offerId => `${LIST_OFFER_URL}/pdf/${offerId}/pdf`}
                onHide={() => setOverviewOfferId(null)}
            />

            <AddAdditionalItemModal
                offer={selectedOffer}
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={handleAddAdditional}
            />

            <PurchaseOrderModal
                show={showPurchaseOrderModal}
                id={purchaseOrderOffer?.id}
                initialValue={purchaseOrderOffer?.purchaseOrderInfluencer}
                onHide={() => setShowPurchaseOrderModal(false)}
                onSubmit={handleSavePurchaseOrder}
            />

            <TableLayout
                pagination={filters}
                listLength={offers.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'offers'}
                skeletonColumns={OFFER_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {OFFER_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {offers.map(offer => (
                            <tr key={offer.id}>
                                <td>{formatDate(offer.createdAt)}</td>
                                <td>{offer.id}</td>
                                <td>
                                    <ImpersonateEye id={offer?.campaign?.company?.createdBy} />
                                    {offer?.campaign?.company?.displayName}
                                </td>
                                <td>
                                    <ImpersonateEye id={offer?.user?.id ?? 0} />
                                    {offer?.user?.displayName}
                                    <br />
                                    {offer?.user?.firstName ?? '-'} {offer?.user?.lastName}
                                </td>
                                <td>
                                    {offer?.campaign?.id}
                                    {renderWithTooltip(offer?.campaign?.title)}
                                </td>
                                <td>
                                    <span className={`status ${offer?.status}`}>{getStatusLabel(offer?.status)}</span>
                                </td>
                                <td>{getOfferType(offer.isAddonsOnly, offer.isLongTerm)}</td>
                                <td>{priceSplitter(offer?.price)} €</td>
                                <td>{offer?.purchaseOrderInfluencer || '-'}</td>
                                <td>
                                    {offer?.user?.agencyCompany?.id ? (
                                        <>
                                            {offer?.user?.agencyCompany?.id}
                                            {renderWithTooltip(offer?.user?.agencyCompany?.displayName)}
                                        </>
                                    ) : (
                                        renderStatusIcon(false)
                                    )}
                                </td>
                                <td>{renderStatusIcon(offer?.isCurrent)}</td>
                                <td>
                                    <DropdownButton title="Actions">
                                        {hasAccessRight(AdminAclEnum.OFFER_OVERVIEW) && (
                                            <DropdownItem onClick={() => setOverviewOfferId(offer.id)}>
                                                Overview
                                            </DropdownItem>
                                        )}
                                        <DropdownItem
                                            onClick={() =>
                                                navigate(`/offers/edit/${offer.id}`, {
                                                    state: { id: offer.id, offer: offer },
                                                })
                                            }
                                        >
                                            Edit
                                        </DropdownItem>
                                        {offer.isCurrent &&
                                            !offer.isAddonsOnly &&
                                            [OfferStatusEnum.ACCEPTED, OfferStatusEnum.COMPLETED].includes(
                                                offer.status as OfferStatusEnum
                                            ) && (
                                                <DropdownItem onClick={() => navigate(`/offers/events/${offer.id}`)}>
                                                    Events
                                                </DropdownItem>
                                            )}
                                        {hasAccessRight(AdminAclEnum.ADD_ADDITIONAL_ITEM) &&
                                            offer.isCurrent &&
                                            offer.isLongTerm === false && (
                                                <DropdownItem
                                                    onClick={() => {
                                                        setSelectedOffer(offer);
                                                        setShowAddModal(true);
                                                    }}
                                                >
                                                    Add additional item
                                                </DropdownItem>
                                            )}
                                        <DropdownItem
                                            onClick={() => {
                                                setPurchaseOrderOffer(offer);
                                                setShowPurchaseOrderModal(true);
                                            }}
                                        >
                                            Add purchase order
                                        </DropdownItem>
                                    </DropdownButton>
                                </td>
                            </tr>
                        ))}
                        {offers.length < 15 && !!offers.length && <div className="h-180px"></div>}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
